import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useApiCall } from '../hooks/useApiCall';
import { RiArrowLeftLine, RiUserLine, RiCalendarLine, RiPriceTag3Line } from 'react-icons/ri';
import '../styles/Blogs.css';

// ─── DOMPurify config (mirrors Page.js) ──────────────────────────────────────
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'strong', 'b', 'em', 'i', 'u', 's',
    'a',
    'img', 'figure', 'figcaption',
    'blockquote', 'footer',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
    'code', 'pre',
    'div', 'span', 'iframe',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'download',
    'src', 'alt', 'width', 'height', 'loading',
    'title', 'frameborder', 'allow', 'allowfullscreen', 'referrerpolicy',
    'style',
    'colspan', 'rowspan',
    'data-type',
    'data-block',
    'data-embed-layout',
  ],
  FORBID_ATTR: ['class', 'id'],
  FORCE_BODY: true,
};

const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'LI', 'TD', 'TH', 'BLOCKQUOTE', 'FOOTER']);

function restrictStyles(node) {
  if (node.tagName === 'IFRAME') {
    try {
      const url = new URL(node.getAttribute('src') || '', window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) {
        node.remove();
        return;
      }
    } catch {
      node.remove();
      return;
    }
    node.setAttribute('loading', 'lazy');
    node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  }
  if (!node.hasAttribute || !node.hasAttribute('style')) return;
  if (node.tagName === 'IMG') {
    const safe = (node.getAttribute('style') || '')
      .split(';')
      .map(s => s.trim())
      .filter(s => /^(width|height|float|display|margin(-left|-right|-top|-bottom)?)\s*:/i.test(s))
      .join('; ');
    safe ? node.setAttribute('style', safe) : node.removeAttribute('style');
  } else if (BLOCK_TAGS.has(node.tagName)) {
    const safe = (node.getAttribute('style') || '')
      .split(';')
      .map(s => s.trim())
      .filter(s => /^text-align\s*:/i.test(s))
      .join('; ');
    safe ? node.setAttribute('style', safe) : node.removeAttribute('style');
  } else {
    node.removeAttribute('style');
  }
}

function sanitizeContent(html) {
  DOMPurify.addHook('afterSanitizeAttributes', restrictStyles);
  const result = DOMPurify.sanitize(html, PURIFY_CONFIG);
  DOMPurify.removeHook('afterSanitizeAttributes');
  return result;
}
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ProjectPost() {
  const { slug } = useParams();
  const apiCall = useApiCall();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiCall('blogs/get-by-slug', 'POST', { slug });
        if (response && response.success && response.data) {
          const b = response.data.blog;
          setBlog(b);

          // SEO
          document.title = `${b.title} - Building Depot Suriname`;

          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute('content', b.meta_description || b.excerpt || '');
          }

          // og:image
          if (b.cover_image) {
            let ogImage = document.querySelector('meta[property="og:image"]');
            if (!ogImage) {
              ogImage = document.createElement('meta');
              ogImage.setAttribute('property', 'og:image');
              document.head.appendChild(ogImage);
            }
            ogImage.setAttribute('content', b.cover_image);
          }
        } else {
          setError('Project not found');
        }
      } catch (err) {
        setError('Failed to load project');
        console.error('Error fetching blog post:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();

    return () => {
      // Reset title when leaving
      document.title = 'Building Depot Suriname';
    };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="blog-post-loading">
            <div className="loading-spinner" />
            <p>Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="blog-post-error">
            <h1>{error || 'Post not found'}</h1>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/projects" className="btn-primary">
              <RiArrowLeftLine /> Back to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const tags = blog.tags ? blog.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <div className="blog-post-page">
      <div className="blog-post-container">

        {/* Header — mirrors .page-header */}
        <div className="blog-post-header">
          <Link to="/projects" className="blog-post-back">
            <RiArrowLeftLine /> Back to Projects
          </Link>

          {tags.length > 0 && (
            <div className="blog-post-tags">
              {tags.map(tag => (
                <Link key={tag} to={`/projects?tag=${encodeURIComponent(tag)}`} className="blog-post-tag">
                  {tag}
                </Link>
              ))}
            </div>
          )}

          <h1 className="blog-post-title">{blog.title}</h1>

          <div className="blog-post-meta">
            {blog.author && (
              <>
                <RiUserLine />
                <span>{blog.author}</span>
              </>
            )}
            {blog.author && blog.created_at && <span className="blog-post-meta-dot" />}
            {blog.created_at && (
              <>
                <RiCalendarLine />
                <span>{formatDate(blog.created_at)}</span>
              </>
            )}
          </div>
        </div>

        {/* Content card — mirrors .page-content-wrapper */}
        <div className="blog-post-card">
          {blog.cover_image && (
            <img
              className="blog-post-cover"
              src={blog.cover_image}
              alt={blog.title}
              loading="eager"
            />
          )}

          {blog.content && (
            <div
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: sanitizeContent(blog.content) }}
            />
          )}

          {(blog.author || blog.created_at || tags.length > 0) && (
            <div className="blog-post-footer">
              {blog.author && (
                <>
                  <RiUserLine />
                  <span>{blog.author}</span>
                </>
              )}
              {blog.created_at && (
                <>
                  <span className="blog-post-meta-dot" />
                  <RiCalendarLine />
                  <span>{formatDate(blog.created_at)}</span>
                </>
              )}
              {tags.length > 0 && (
                <>
                  <span className="blog-post-meta-dot" />
                  <RiPriceTag3Line />
                  <span className="blog-post-footer-tags">
                    {tags.map((tag, index) => (
                      <span key={tag}>
                        {index > 0 && ', '}
                        <Link to={`/projects?tag=${encodeURIComponent(tag)}`}>{tag}</Link>
                      </span>
                    ))}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
